import fp from "lodash/fp"

import type {
  DevicePlotDef,
  GaugePlotView,
  LinePlotView,
  MultiValueStatPlotView,
  PlotTabsConfig,
  ScatterPlotView,
  SingleStatPlotView,
  TableStatPlotView,
  WeatherServicePlotDef,
} from "types/dashboard.types"
import type { CustomTooltipProps } from "types/viewProperties.types"
import type { PositionFieldMapping } from "types/devicePosition.types"

import { darkTheme } from "styles/themes"
import SagDialogContent from "widgets/plots/dialogs/SagDialogContent"
import ConductorTensionDialogContent from "widgets/plots/dialogs/ConductorTensionDialogContent"
import IcingDialogContent from "widgets/plots/dialogs/IcingDialogContent"
import MuiIcingTooltip from "widgets/plots/mui/icing/MuiIcingTooltip"
import MuiWSDegreesRadiansTooltip from "widgets/plots/mui/ws/MuiWSDegreesRadiansTooltip"
import { ICE_TYPE_FIELD_PROPERTIES, ICE_TYPES_BY_SEVERITY } from "helpers/utils/icing"
import { getFieldFormat } from "helpers/utils/telemetryFields"
import {
  gaugeView,
  lineView,
  scatterView,
  singleStatView,
} from "helpers/config/dashboards/base/views"
import {
  OPEN_METEO_ATTRIBUTION,
  linePlotDefBase,
  scatterPlotDefBase,
  singleStatPlotDefBase,
  tableStatPlotDefBase,
} from "helpers/config/dashboards/base/defs"
import {
  AGGREGATED_INCLINATION_FIELD,
  BATTERY_LEVEL_FIELD,
  BATTERY_STATUS_FIELD,
  BENDING_MAX_DISPLACEMENT_FIELD,
  CABLE_TEMPERATURE_FIELD,
  CUMULATIVE_FATIGUE_FIELD,
  DEVICE_CURRENT_FIELD,
  FRONT_TILT_DRIFT_INSTANT_FIELD,
  FRONT_TILT_DRIFT_SMOOTHED_FIELD,
  ICE_ACCRETION_SEVERITY_FIELD,
  MAGNITUDE_TILT_DRIFT_INSTANT_FIELD,
  MAGNITUDE_TILT_DRIFT_SMOOTHED_FIELD,
  MOTION_ALERT_FIELD,
  MOTION_ALPHA_FIELD,
  MOTION_BETA_FIELD,
  MOTION_JERK_NORM_FIELD,
  NET_NAME_FIELD,
  NET_SIGNAL_FIELD,
  NET_TRANSPORT_FIELD,
  RIGHT_TILT_DRIFT_INSTANT_FIELD,
  RIGHT_TILT_DRIFT_SMOOTHED_FIELD,
  SAG_HORIZONTAL_TENSION_FIELD,
  SAG_LOWEST_POINT_FIELD,
  SAG_MIDPOINT_FIELD,
  SAG_TENSION_LOWER_SUPPORT_FIELD,
  SAG_TENSION_UPPER_SUPPORT_FIELD,
  SAG_THERMAL_HORIZONTAL_TENSION_FIELD,
  SAG_THERMAL_LOWEST_POINT_FIELD,
  SAG_THERMAL_MIDPOINT_FIELD,
  SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD,
  SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD,
  SAG_THERMAL_VALUE_FIELD,
  SAG_VALUE_FIELD,
  TILT_FRONT_FIELD,
  TILT_FRONT_SMOOTHED_FIELD,
  TILT_HEADING_FIELD,
  TILT_HEADING_SMOOTHED_FIELD,
  TILT_MAGNITUDE_FIELD,
  TILT_MAGNITUDE_SMOOTHED_FIELD,
  TILT_RIGHT_FIELD,
  TILT_RIGHT_SMOOTHED_FIELD,
  TRANSVERSE_WIND_SPEED_FIELD,
  WSERVICE_HUMIDITY_FIELD,
  WSERVICE_PRECIPITATION_FIELD,
  WSERVICE_TEMPERATURE_FIELD,
  WSERVICE_WIND_DIRECTION_FIELD,
  WSERVICE_WIND_GUSTS_FIELD,
  WSERVICE_WIND_SPEED_FIELD,
  WSTATION_HUMIDITY_FIELD,
  WSTATION_PRECIPITATION_FIELD,
  WSTATION_PRESSURE_FIELD,
  WSTATION_SOLAR_IRRADIANCE_FIELD,
  WSTATION_TEMPERATURE_FIELD,
  WSTATION_WIND_DIRECTION_FIELD,
  WSTATION_WIND_SPEED_FIELD,
} from "helpers/config/telemetryFieldDefs"

// ─── Position ─────────────────────────────────────────────────────────────────

const POSITION_FIELDS = [MOTION_ALPHA_FIELD, MOTION_BETA_FIELD]

const positionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Position",
  nameKey: "POSITION",
  properties: {
    axes: {
      y: {
        bounds: ["-5", "5"],
        ...getFieldFormat(POSITION_FIELDS),
      },
    },
  },
})

// --- Powerline

const positionFieldProperties = {
  [`{"_field":"${MOTION_ALPHA_FIELD}"}`]: {
    color: darkTheme.palette.alpha.main,
    labelKey: "plot_titles.ALPHA",
  },
  [`{"_field":"${MOTION_BETA_FIELD}"}`]: {
    color: darkTheme.palette.beta.main,
    labelKey: "plot_titles.BETA",
  },
}

export const positionInPowerlinePlotView: LinePlotView = fp.merge(positionPlotView, {
  helpTooltip: ["plots_descriptions.POSITION_IN_POWERLINE"],
  properties: {
    fieldProperties: positionFieldProperties,
  },
})

export const positionReqConfig = {
  fields: POSITION_FIELDS,
  aggregate: ["last" as const],
  aggregate_n: 180,
  aggregate_use_original_time: true,
  group: ["id", "_field"],
}

export const positionInPowerlinePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: positionInPowerlinePlotView,
  reqConfig: positionReqConfig,
  draggable: false,
})

// --- Tower

export const legacyPositionInTowerPlotView: LinePlotView = fp.merge(positionPlotView, {
  helpTooltip: ["plots_descriptions.POSITION_IN_TOWER"],
  properties: {
    fieldProperties: positionFieldProperties,
  },
})

export const legacyPositionInTowerPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: legacyPositionInTowerPlotView,
  reqConfig: positionReqConfig,
  draggable: false,
})

const TOWER_TILT_FIELDS = [
  TILT_FRONT_FIELD,
  TILT_RIGHT_FIELD,
  TILT_MAGNITUDE_FIELD,
  TILT_HEADING_FIELD,
  TILT_FRONT_SMOOTHED_FIELD,
  TILT_RIGHT_SMOOTHED_FIELD,
  TILT_MAGNITUDE_SMOOTHED_FIELD,
  TILT_HEADING_SMOOTHED_FIELD,
]

export const TOWER_TILT_FIELDS_INSTANT: PositionFieldMapping = {
  first: TILT_FRONT_FIELD,
  second: TILT_RIGHT_FIELD,
}

export const MAGNITUDE_HEADING_FIELDS: PositionFieldMapping = {
  first: TILT_MAGNITUDE_FIELD,
  second: TILT_HEADING_FIELD,
}

export const positionInTowerPlotView: LinePlotView = fp.merge(positionPlotView, {
  name: "Tilts",
  nameKey: "TILTS",
  helpTooltip: [
    "plots_descriptions.DEVICE_POSITION_IN_TOWER.FRONT_TILT",
    "plots_descriptions.DEVICE_POSITION_IN_TOWER.RIGHT_TILT",
    "plots_descriptions.DEVICE_POSITION_IN_TOWER.TILT_MAGNITUDE",
    "plots_descriptions.DEVICE_POSITION_IN_TOWER.HEADING",
  ],
  properties: {
    fieldProperties: {
      [`{"_field":"${TILT_FRONT_FIELD}"}`]: {
        color: darkTheme.palette.alpha.main,
      },
      [`{"_field":"${TILT_RIGHT_FIELD}"}`]: {
        color: darkTheme.palette.beta.main,
      },
      [`{"_field":"${TILT_MAGNITUDE_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
      },
      [`{"_field":"${TILT_FRONT_SMOOTHED_FIELD}"}`]: {
        color: darkTheme.palette.alpha.main,
      },
      [`{"_field":"${TILT_RIGHT_SMOOTHED_FIELD}"}`]: {
        color: darkTheme.palette.beta.main,
      },
      [`{"_field":"${TILT_MAGNITUDE_SMOOTHED_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
      },
    },
  },
})

export const towerPositionTabsConfig: PlotTabsConfig = {
  tabs: [
    {
      id: "instant",
      labelKey: "plot_legends.INSTANT",
      tooltipKey: "plot_legends.POSITION_INSTANT_TOOLTIP",
      fields: [TILT_FRONT_FIELD, TILT_RIGHT_FIELD, TILT_MAGNITUDE_FIELD],
    },
    {
      id: "smoothed",
      labelKey: "plot_legends.SMOOTHED",
      tooltipKey: "plot_legends.POSITION_SMOOTHED_TOOLTIP",
      fields: [
        TILT_FRONT_SMOOTHED_FIELD,
        TILT_RIGHT_SMOOTHED_FIELD,
        TILT_MAGNITUDE_SMOOTHED_FIELD,
      ],
    },
  ],
  defaultTab: "instant",
}

export const towerPositionReqConfig = {
  fields: TOWER_TILT_FIELDS,
  aggregate: ["last" as const],
  aggregate_n: 180,
  aggregate_use_original_time: true,
  group: ["id", "_field"],
  measurement: "tower_health",
}

export const positionInTowerPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: positionInTowerPlotView,
  reqConfig: towerPositionReqConfig,
  draggable: false,
  tabsConfig: towerPositionTabsConfig,
})

// ─── Amplitude & drift ────────────────────────────────────────────────────────

export const amplitudeReqConfig = {
  fields: POSITION_FIELDS,
  method: ["spread" as const],
  group: ["id", "_field"],
}

export const alphaAmplitudePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Alpha amplitude",
  nameKey: "ALPHA_AMPLITUDE",
  helpTooltip: ["plots_descriptions.ANGLE_AMPLITUDE"],
  properties: {
    size: "md",
    ...getFieldFormat(POSITION_FIELDS),
  },
})

export const alphaAmplitudePlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: alphaAmplitudePlotView,
  reqConfig: amplitudeReqConfig,
  draggable: false,
})

export const betaAmplitudePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Beta amplitude",
  nameKey: "BETA_AMPLITUDE",
  helpTooltip: ["plots_descriptions.ANGLE_AMPLITUDE"],
  properties: {
    size: "md",
    ...getFieldFormat(POSITION_FIELDS),
  },
})

export const betaAmplitudePlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: betaAmplitudePlotView,
  reqConfig: amplitudeReqConfig,
  draggable: false,
})

export const alphaDriftPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Alpha drift",
  nameKey: "ALPHA_DRIFT",
  helpTooltip: ["plots_descriptions.ANGLE_DRIFT"],
  properties: {
    size: "md",
    ...getFieldFormat(POSITION_FIELDS),
  },
})

export const alphaDriftPlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: alphaDriftPlotView,
  reqConfig: positionReqConfig,
  draggable: false,
})

export const betaDriftPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Beta drift",
  nameKey: "BETA_DRIFT",
  helpTooltip: ["plots_descriptions.ANGLE_DRIFT"],
  properties: {
    size: "md",
    ...getFieldFormat(POSITION_FIELDS),
  },
})

export const betaDriftPlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: betaDriftPlotView,
  reqConfig: positionReqConfig,
  draggable: false,
})

// --- Tower tilt stats (table stat)

export const towerAmplitudeReqConfig = {
  fields: [
    TILT_FRONT_FIELD,
    TILT_RIGHT_FIELD,
    TILT_MAGNITUDE_FIELD,
    TILT_FRONT_SMOOTHED_FIELD,
    TILT_RIGHT_SMOOTHED_FIELD,
    TILT_MAGNITUDE_SMOOTHED_FIELD,
  ],
  method: ["spread" as const],
  group: ["id", "_field"],
  measurement: "tower_health",
}

// Drift is computed client-side. The synthetic _field values below are used as
// keys between the parent component and the tilt-stats table-stat rows.

const tiltStatsTableViewBase: Omit<TableStatPlotView, "name" | "nameKey"> = {
  type: "table-stat",
  helpTooltip: [
    "plots_descriptions.TILT_STATS_AMPLITUDE",
    "plots_descriptions.TILT_STATS_DRIFT",
  ],
  properties: {
    ...getFieldFormat(TOWER_TILT_FIELDS),
    columns: [
      { key: "metric", labelKey: "table_stats.METRIC", type: "label" },
      { key: "method", labelKey: "table_stats.METHOD", type: "label" },
      { key: "value", labelKey: "table_stats.VALUE", type: "value" },
    ],
    rows: [],
  },
}

export const frontTiltStatsTableDef: DevicePlotDef = {
  ...tableStatPlotDefBase,
  draggable: false,
  view: {
    ...tiltStatsTableViewBase,
    name: "Front tilt stats",
    nameKey: "FRONT_TILT_STATS",
    properties: {
      ...tiltStatsTableViewBase.properties,
      rows: [
        {
          field: TILT_FRONT_FIELD,
          labelKey: "table_stats.AMPLITUDE",
          labels: { method: "plot_legends.INSTANT" },
        },
        {
          field: TILT_FRONT_SMOOTHED_FIELD,
          labelKey: "table_stats.AMPLITUDE",
          labels: { method: "plot_legends.SMOOTHED" },
        },
        {
          field: FRONT_TILT_DRIFT_INSTANT_FIELD,
          labelKey: "table_stats.DRIFT",
          labels: { method: "plot_legends.INSTANT" },
        },
        {
          field: FRONT_TILT_DRIFT_SMOOTHED_FIELD,
          labelKey: "table_stats.DRIFT",
          labels: { method: "plot_legends.SMOOTHED" },
        },
      ],
    },
  } as TableStatPlotView,
  reqConfig: {
    fields: [TILT_FRONT_FIELD, TILT_FRONT_SMOOTHED_FIELD],
    method: ["spread"],
    group: ["id", "_field"],
    measurement: "tower_health",
  },
}

export const rightTiltStatsTableDef: DevicePlotDef = {
  ...tableStatPlotDefBase,
  draggable: false,
  view: {
    ...tiltStatsTableViewBase,
    name: "Right tilt stats",
    nameKey: "RIGHT_TILT_STATS",
    properties: {
      ...tiltStatsTableViewBase.properties,
      rows: [
        {
          field: TILT_RIGHT_FIELD,
          labelKey: "table_stats.AMPLITUDE",
          labels: { method: "plot_legends.INSTANT" },
        },
        {
          field: TILT_RIGHT_SMOOTHED_FIELD,
          labelKey: "table_stats.AMPLITUDE",
          labels: { method: "plot_legends.SMOOTHED" },
        },
        {
          field: RIGHT_TILT_DRIFT_INSTANT_FIELD,
          labelKey: "table_stats.DRIFT",
          labels: { method: "plot_legends.INSTANT" },
        },
        {
          field: RIGHT_TILT_DRIFT_SMOOTHED_FIELD,
          labelKey: "table_stats.DRIFT",
          labels: { method: "plot_legends.SMOOTHED" },
        },
      ],
    },
  } as TableStatPlotView,
  reqConfig: {
    fields: [TILT_RIGHT_FIELD, TILT_RIGHT_SMOOTHED_FIELD],
    method: ["spread"],
    group: ["id", "_field"],
    measurement: "tower_health",
  },
}

export const magnitudeTiltStatsTableDef: DevicePlotDef = {
  ...tableStatPlotDefBase,
  draggable: false,
  view: {
    ...tiltStatsTableViewBase,
    name: "Magnitude stats",
    nameKey: "MAGNITUDE_TILT_STATS",
    properties: {
      ...tiltStatsTableViewBase.properties,
      rows: [
        {
          field: TILT_MAGNITUDE_FIELD,
          labelKey: "table_stats.AMPLITUDE",
          labels: { method: "plot_legends.INSTANT" },
        },
        {
          field: TILT_MAGNITUDE_SMOOTHED_FIELD,
          labelKey: "table_stats.AMPLITUDE",
          labels: { method: "plot_legends.SMOOTHED" },
        },
        {
          field: MAGNITUDE_TILT_DRIFT_INSTANT_FIELD,
          labelKey: "table_stats.DRIFT",
          labels: { method: "plot_legends.INSTANT" },
        },
        {
          field: MAGNITUDE_TILT_DRIFT_SMOOTHED_FIELD,
          labelKey: "table_stats.DRIFT",
          labels: { method: "plot_legends.SMOOTHED" },
        },
      ],
    },
  } as TableStatPlotView,
  reqConfig: {
    fields: [TILT_MAGNITUDE_FIELD, TILT_MAGNITUDE_SMOOTHED_FIELD],
    method: ["spread"],
    group: ["id", "_field"],
    measurement: "tower_health",
  },
}

// ─── Motion detection ─────────────────────────────────────────────────────────

const motionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Motion detection",
  nameKey: "MOTION_DETECTION",
  helpTooltip: ["plots_descriptions.MOTION"],
  properties: {
    connectNulls: false,
    axes: {
      y: {
        bounds: ["0", "1"],
        label: "Motion change and state",
        base: "",
        formatUnits: false,
        totalTicks: 1,
      },
    },
    fieldProperties: {
      [MOTION_ALERT_FIELD]: { curve: "stepAfter" },
    },
  },
})

export const motionPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: motionPlotView,
  reqConfig: {
    fields: [MOTION_ALERT_FIELD],
    aggregate: ["max"],
    group: ["id", "_field"],
    method: ["toInt"],
  },
})

// ─── Motion intensity ─────────────────────────────────────────────────────────

const motionIntensityPlotView: ScatterPlotView = fp.merge(scatterView, {
  name: "Motion intensity",
  nameKey: "MOTION_INTENSITY",
  type: "scatter",
  helpTooltip: ["plots_descriptions.MOTION_INTENSITY"],
  properties: {
    axes: {
      y: {
        bounds: ["0", "2"],
        ...getFieldFormat([MOTION_JERK_NORM_FIELD]),
      },
    },
  },
})

export const motionIntensityPlotDef: DevicePlotDef = fp.merge(scatterPlotDefBase, {
  view: motionIntensityPlotView,
  draggable: true,
  reqConfig: {
    fields: [MOTION_JERK_NORM_FIELD],
    aggregate: ["max"],
    group: ["id", "_field"],
    allowRawData: false,
    fetchFieldMetadata: false,
  },
})

// ─── Battery level ────────────────────────────────────────────────────────────

const batteryLevelPlotView: LinePlotView = fp.merge(lineView, {
  name: "Battery",
  nameKey: "BATTERY",
  properties: {
    axes: {
      y: {
        bounds: ["0", "100"],
        ...getFieldFormat([BATTERY_LEVEL_FIELD]),
      },
    },
  },
})

export const batteryLevelPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: batteryLevelPlotView,
  reqConfig: {
    fields: [BATTERY_LEVEL_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

// ─── Battery status ───────────────────────────────────────────────────────────

const batteryStatusPlotView: GaugePlotView = fp.merge(gaugeView, {
  name: "Battery Status",
  nameKey: "BATTERY_STATUS",
  properties: {
    footerFields: ["_time"],
    ticksValueInterval: 20,
    suffix: "%",
    tickSuffix: "%",
  },
})

export const batteryStatusPlotDef: DevicePlotDef = {
  view: batteryStatusPlotView,
  draggable: true,
  dataSource: { entity: "device", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [BATTERY_LEVEL_FIELD, BATTERY_STATUS_FIELD],
    method: ["last"],
    group: ["id", "_field"],
  },
}

// ─── Signal status ────────────────────────────────────────────────────────────

const signalStatusPlotView: GaugePlotView = fp.merge(gaugeView, {
  name: "Signal level [dBm]",
  nameKey: "SIGNAL_LEVEL",
  properties: {
    footerFields: ["_time"],
    ticksLabelCount: 6,
    suffix: "dBm",
    colors: [
      { id: "0", type: "min", value: -120 },
      { id: "1", type: "max", value: -35 },
    ],
  },
})

export const signalStatusPlotDef: DevicePlotDef = {
  view: signalStatusPlotView,
  draggable: true,
  dataSource: { entity: "device", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [NET_SIGNAL_FIELD, NET_TRANSPORT_FIELD, NET_NAME_FIELD],
    method: ["last"],
    group: ["id", "_field"],
  },
}

// ─── Average inclination ──────────────────────────────────────────────────────

const averageInclinationPlotView: LinePlotView = fp.merge(lineView, {
  name: "Average inclination",
  nameKey: "AVERAGE_INCLINATION",
  helpTooltip: ["plots_descriptions.AVERAGE_INCLINATION"],
  properties: {
    axes: {
      y: {
        bounds: ["0", "5"],
        ...getFieldFormat([AGGREGATED_INCLINATION_FIELD]),
      },
    },
  },
})

export const averageInclinationPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: averageInclinationPlotView,
  reqConfig: {
    fields: [AGGREGATED_INCLINATION_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

// ─── Conductor temperature ────────────────────────────────────────────────────

const conductorTemperaturePlotView: LinePlotView = fp.merge(lineView, {
  name: "Conductor temperature",
  nameKey: "CONDUCTOR_TEMPERATURE",
  helpTooltip: ["plots_descriptions.CONDUCTOR_TEMPERATURE"],
  properties: {
    axes: {
      y: getFieldFormat([CABLE_TEMPERATURE_FIELD]),
    },
  },
})

export const conductorTemperaturePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: conductorTemperaturePlotView,
  reqConfig: {
    fields: [CABLE_TEMPERATURE_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastConductorTemperaturePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last conductor temperature",
  nameKey: "LAST_CONDUCTOR_TEMPERATURE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([CABLE_TEMPERATURE_FIELD]),
    size: "lg",
  },
})

export const lastConductorTemperaturePlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastConductorTemperaturePlotView,
    reqConfig: {
      fields: [CABLE_TEMPERATURE_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Bending amplitude ────────────────────────────────────────────────────────

const bendingAmplitudePlotView = fp.merge(lineView, {
  name: "Bending amplitude",
  nameKey: "BENDING_AMPLITUDE",
  helpTooltip: [
    "plots_descriptions.BENDING_AMPLITUDE",
    "plots_descriptions.BENDING_AMPLITUDE_LIMIT",
  ],
  properties: {
    axes: {
      y: getFieldFormat([BENDING_MAX_DISPLACEMENT_FIELD]),
    },
  },
})

export const bendingAmplitudePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: bendingAmplitudePlotView,
  reqConfig: {
    fields: [BENDING_MAX_DISPLACEMENT_FIELD],
    aggregate: ["max"],
    group: ["id", "_field"],
  },
})

const maxBendingAmplitudePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Max bending amplitude",
  nameKey: "MAX_BENDING_AMPLITUDE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([BENDING_MAX_DISPLACEMENT_FIELD]),
    size: "md",
  },
})

export const maxBendingAmplitudePlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: maxBendingAmplitudePlotView,
  reqConfig: {
    fields: [BENDING_MAX_DISPLACEMENT_FIELD],
    method: ["max"],
    group: ["id", "_field"],
  },
})

const dataPercentageAboveLimitPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "% Above Limit",
  nameKey: "PERCENTAGE_ABOVE_BENDING_AMPLITUDE_LIMIT",
  helpTooltip: ["plots_descriptions.PERCENTAGE_ABOVE_BENDING_AMPLITUDE_LIMIT"],
  properties: {
    suffix: "%",
    size: "lg",
    decimalPlaces: 2,
    dataTransform: {
      type: "percentage",
      condition: "above",
    },
    footerText: ["plot_legends.WITHIN_TIMEFRAME"],
  },
})

export const dataPercentageAboveLimitPlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: dataPercentageAboveLimitPlotView,
    reqConfig: {
      fields: [BENDING_MAX_DISPLACEMENT_FIELD],
      aggregate: ["max"],
      group: ["id", "_field"],
    },
  },
)

// ─── Cumulative fatigue ───────────────────────────────────────────────────────

const cumulativeFatigueFormat = getFieldFormat([CUMULATIVE_FATIGUE_FIELD])

// The axis can only carry as many digits as a tick label fits, which is not enough to tell
// consecutive days apart. The tooltip and the stat have the room, so they show more.
const CUMULATIVE_FATIGUE_EXACT_DECIMALS = 6

const cumulativeFatiguePlotView: LinePlotView = fp.merge(lineView, {
  name: "Cumulative Fatigue",
  nameKey: "CUMULATIVE_FATIGUE",
  helpTooltip: ["plots_descriptions.CUMULATIVE_FATIGUE"],
  properties: {
    axes: {
      y: {
        ...cumulativeFatigueFormat,
        useScientificNotation: true,
      },
    },
    tooltip: {
      trigger: "axis",
      decimalPlaces: CUMULATIVE_FATIGUE_EXACT_DECIMALS,
    },
  },
})

export const cumulativeFatiguePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: cumulativeFatiguePlotView,
  reqConfig: {
    fields: [CUMULATIVE_FATIGUE_FIELD],
    aggregate: ["max"],
    group: ["id", "_field"],
  },
})

const lastCumulativeFatiguePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last cumulative fatigue",
  nameKey: "LAST_CUMULATIVE_FATIGUE",
  properties: {
    footerFields: ["_time"],
    ...cumulativeFatigueFormat,
    decimalPlaces: CUMULATIVE_FATIGUE_EXACT_DECIMALS,
    useScientificNotation: true,
    size: "lg",
  },
})

export const lastCumulativeFatiguePlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastCumulativeFatiguePlotView,
    reqConfig: {
      fields: [CUMULATIVE_FATIGUE_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Ice accretion detection ──────────────────────────────────────────────────

export const iceAccretionDetectionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Ice accretion detection",
  nameKey: "ICE_ACCRETION_DETECTION",
  dialogTitleKey: "plots_descriptions.ICING_DIALOG.TITLE",
  dialogContent: () => <IcingDialogContent />,
  properties: {
    fill: ["_field", "_value"],
    connectNulls: false,
    axes: {
      y: {
        bounds: ["0", "5"],
        base: "",
        formatUnits: false,
        valueFormatter: (value: number, t?: (key: string) => string) => {
          if (!t) return String(value)
          const iceType = ICE_TYPES_BY_SEVERITY[value]
          return iceType ? t(iceType.shortLabelKey) : String(value)
        },
      },
    },
    fieldProperties: {
      [ICE_ACCRETION_SEVERITY_FIELD]: { curve: "stepAfter" },
      ...ICE_TYPE_FIELD_PROPERTIES,
    },
    tooltip: {
      trigger: "axis",
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiIcingTooltip trigger={trigger} />
      ),
    },
  },
})

export const iceAccretionDetectionPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: iceAccretionDetectionPlotView,
  reqConfig: {
    fields: [ICE_ACCRETION_SEVERITY_FIELD],
    aggregate: ["max"],
    group: ["id", "_field"],
  },
})

const lastIceAccretionDetectionPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last ice accretion detection",
  nameKey: "LAST_ICE_ACCRETION_DETECTION",
  properties: {
    footerFields: ["_time", "_value"],
    size: "lg",
  },
})

export const lastIceAccretionDetectionPlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastIceAccretionDetectionPlotView,
    reqConfig: {
      fields: [ICE_ACCRETION_SEVERITY_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Transverse wind speed ────────────────────────────────────────────────────

const transverseWindSpeedPlotView: LinePlotView = fp.merge(lineView, {
  name: "Transverse wind speed",
  nameKey: "TRANSVERSE_WIND_SPEED",
  helpTooltip: ["plots_descriptions.TRANSVERSE_WIND_SPEED"],
  properties: {
    axes: {
      y: getFieldFormat([TRANSVERSE_WIND_SPEED_FIELD]),
    },
  },
})

export const transverseWindSpeedPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: transverseWindSpeedPlotView,
  reqConfig: {
    fields: [TRANSVERSE_WIND_SPEED_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastWindSpeedPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind speed value",
  nameKey: "LAST_T_WIND_SPEED",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([TRANSVERSE_WIND_SPEED_FIELD]),
    size: "lg",
  },
})

export const lastWindSpeedPlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: lastWindSpeedPlotView,
  reqConfig: {
    fields: [TRANSVERSE_WIND_SPEED_FIELD],
    method: ["last"],
    group: ["id", "_field"],
  },
})

// ─── Sag ──────────────────────────────────────────────────────────────────────

const STANDARD_SAG_FIELDS = [SAG_VALUE_FIELD, SAG_LOWEST_POINT_FIELD, SAG_MIDPOINT_FIELD]
const DUAL_SAG_FIELDS = [
  ...STANDARD_SAG_FIELDS,
  SAG_THERMAL_VALUE_FIELD,
  SAG_THERMAL_LOWEST_POINT_FIELD,
  SAG_THERMAL_MIDPOINT_FIELD,
]

const standardSagPlotView: LinePlotView = fp.merge(lineView, {
  name: "Sag",
  nameKey: "SAG",
  helpTooltip: ["plots_descriptions.SAG"],
  dialogTitleKey: "plots_descriptions.SAG_DIAGRAM.TITLE",
  dialogContent: () => <SagDialogContent />,
  properties: {
    axes: {
      y: getFieldFormat(STANDARD_SAG_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${SAG_VALUE_FIELD}"}`]: { color: darkTheme.palette.primary.main },
      [`{"_field":"${SAG_LOWEST_POINT_FIELD}"}`]: {
        color: darkTheme.palette.secondary.main,
      },
      [`{"_field":"${SAG_MIDPOINT_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
      },
    },
  },
})

export const standardSagPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: standardSagPlotView,
  reqConfig: {
    fields: STANDARD_SAG_FIELDS,
    aggregate: ["max"],
    group: ["id", "_field"],
  },
})

const dualSagTabsConfig: PlotTabsConfig = {
  tabs: [
    {
      id: "max_sag",
      labelKey: "sag_plots.MAX_SAG",
      fields: [SAG_VALUE_FIELD, SAG_THERMAL_VALUE_FIELD],
    },
    {
      id: "lowest_point",
      labelKey: "sag_plots.LOWEST_POINT",
      fields: [SAG_LOWEST_POINT_FIELD, SAG_THERMAL_LOWEST_POINT_FIELD],
    },
    {
      id: "midpoint",
      labelKey: "sag_plots.MIDPOINT",
      fields: [SAG_MIDPOINT_FIELD, SAG_THERMAL_MIDPOINT_FIELD],
    },
  ],
  defaultTab: "max_sag",
  showAllTab: true,
}

const dualSagPlotView: LinePlotView = fp.merge(lineView, {
  name: "Sag",
  nameKey: "SAG",
  helpTooltip: ["plots_descriptions.SAG", "plots_descriptions.DUAL_SAG"],
  dialogTitleKey: "plots_descriptions.SAG_DIAGRAM.TITLE",
  dialogContent: () => <SagDialogContent />,
  properties: {
    axes: {
      y: getFieldFormat(DUAL_SAG_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${SAG_VALUE_FIELD}"}`]: {
        color: darkTheme.palette.primary.main,
        labelKey: "sag_plots.MAX_SAG_INCLINATION",
      },
      [`{"_field":"${SAG_LOWEST_POINT_FIELD}"}`]: {
        color: darkTheme.palette.secondary.main,
        labelKey: "sag_plots.LOWEST_POINT_INCLINATION",
      },
      [`{"_field":"${SAG_MIDPOINT_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
        labelKey: "sag_plots.MIDPOINT_INCLINATION",
      },
      [`{"_field":"${SAG_THERMAL_VALUE_FIELD}"}`]: {
        color: darkTheme.palette.extras.cinnabar.main,
        labelKey: "sag_plots.MAX_SAG_THERMAL",
      },
      [`{"_field":"${SAG_THERMAL_LOWEST_POINT_FIELD}"}`]: {
        color: darkTheme.palette.extras.marigold.main,
        labelKey: "sag_plots.LOWEST_POINT_THERMAL",
      },
      [`{"_field":"${SAG_THERMAL_MIDPOINT_FIELD}"}`]: {
        color: darkTheme.palette.extras.tangerine.main,
        labelKey: "sag_plots.MIDPOINT_THERMAL",
      },
    },
  },
})

export const dualSagPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: dualSagPlotView,
  reqConfig: {
    fields: DUAL_SAG_FIELDS,
    aggregate: ["max"],
    group: ["id", "_field"],
  },
  tabsConfig: dualSagTabsConfig,
})

const sagMultiReqConfigBase = {
  method: ["last"],
  group: ["id", "_field"],
}

const sagMultiViewBase: Omit<MultiValueStatPlotView, "name" | "nameKey"> = {
  type: "multi-value-stat",
  properties: {
    rows: [],
    ...getFieldFormat(DUAL_SAG_FIELDS),
  },
}

export const lastMaxSagMultiDef: DevicePlotDef = {
  ...singleStatPlotDefBase,
  view: {
    ...sagMultiViewBase,
    name: "Last max sag",
    nameKey: "LAST_MAX_SAG",
    properties: {
      ...sagMultiViewBase.properties,
      rows: [
        {
          field: SAG_VALUE_FIELD,
          labelKey: "sag_plots.INCLINATION_BASED",
          color: darkTheme.palette.primary.main,
        },
        {
          field: SAG_THERMAL_VALUE_FIELD,
          labelKey: "sag_plots.THERMAL_BASED",
          color: darkTheme.palette.extras.cinnabar.main,
        },
      ],
    },
  } as MultiValueStatPlotView,
  reqConfig: {
    fields: [SAG_VALUE_FIELD, SAG_THERMAL_VALUE_FIELD],
    ...sagMultiReqConfigBase,
  },
}

export const lastLowestPointSagMultiDef: DevicePlotDef = {
  ...singleStatPlotDefBase,
  view: {
    ...sagMultiViewBase,
    name: "Last lowest sag point",
    nameKey: "LAST_LOWEST_POINT_SAG",
    properties: {
      ...sagMultiViewBase.properties,
      rows: [
        {
          field: SAG_LOWEST_POINT_FIELD,
          labelKey: "sag_plots.INCLINATION_BASED",
          color: darkTheme.palette.secondary.main,
        },
        {
          field: SAG_THERMAL_LOWEST_POINT_FIELD,
          labelKey: "sag_plots.THERMAL_BASED",
          color: darkTheme.palette.extras.marigold.main,
        },
      ],
    },
  } as MultiValueStatPlotView,
  reqConfig: {
    fields: [SAG_LOWEST_POINT_FIELD, SAG_THERMAL_LOWEST_POINT_FIELD],
    ...sagMultiReqConfigBase,
  },
}

export const lastMidpointSagMultiDef: DevicePlotDef = {
  ...singleStatPlotDefBase,
  view: {
    ...sagMultiViewBase,
    name: "Last midspan sag",
    nameKey: "LAST_MIDPOINT_SAG",
    properties: {
      ...sagMultiViewBase.properties,
      rows: [
        {
          field: SAG_MIDPOINT_FIELD,
          labelKey: "sag_plots.INCLINATION_BASED",
          color: darkTheme.palette.extras.limeGreen.main,
        },
        {
          field: SAG_THERMAL_MIDPOINT_FIELD,
          labelKey: "sag_plots.THERMAL_BASED",
          color: darkTheme.palette.extras.tangerine.main,
        },
      ],
    },
  } as MultiValueStatPlotView,
  reqConfig: {
    fields: [SAG_MIDPOINT_FIELD, SAG_THERMAL_MIDPOINT_FIELD],
    ...sagMultiReqConfigBase,
  },
}

// ─── Conductor tension ────────────────────────────────────────────────────────

const dualConductorTensionTabsConfig: PlotTabsConfig = {
  tabs: [
    {
      id: "horizontal_tension",
      labelKey: "sag_plots.HORIZONTAL_TENSION",
      fields: [SAG_HORIZONTAL_TENSION_FIELD, SAG_THERMAL_HORIZONTAL_TENSION_FIELD],
    },
    {
      id: "tension_at_the_lower_support",
      labelKey: "sag_plots.TENSION_AT_THE_LOWER_SUPPORT",
      fields: [SAG_TENSION_LOWER_SUPPORT_FIELD, SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD],
    },
    {
      id: "tension_at_the_upper_support",
      labelKey: "sag_plots.TENSION_AT_THE_UPPER_SUPPORT",
      fields: [SAG_TENSION_UPPER_SUPPORT_FIELD, SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD],
    },
  ],
  defaultTab: "horizontal_tension",
  showAllTab: true,
}

const STANDARD_CONDUCTOR_TENSION_FIELDS = [
  SAG_HORIZONTAL_TENSION_FIELD,
  SAG_TENSION_LOWER_SUPPORT_FIELD,
  SAG_TENSION_UPPER_SUPPORT_FIELD,
]
const DUAL_CONDUCTOR_TENSION_FIELDS = [
  ...STANDARD_CONDUCTOR_TENSION_FIELDS,
  SAG_THERMAL_HORIZONTAL_TENSION_FIELD,
  SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD,
  SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD,
]

const dualConductorTensionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Conductor tension",
  nameKey: "CONDUCTOR_TENSION",
  helpTooltip: [
    "plots_descriptions.HORIZONTAL_TENSION",
    "plots_descriptions.DUAL_CONDUCTOR_TENSION",
  ],
  dialogTitleKey: "plots_descriptions.CONDUCTOR_TENSION_DIAGRAM.TITLE",
  dialogContent: () => <ConductorTensionDialogContent />,
  properties: {
    axes: {
      y: getFieldFormat(DUAL_CONDUCTOR_TENSION_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${SAG_HORIZONTAL_TENSION_FIELD}"}`]: {
        color: darkTheme.palette.primary.main,
        labelKey: "sag_plots.HORIZONTAL_TENSION_INCLINATION",
      },
      [`{"_field":"${SAG_TENSION_LOWER_SUPPORT_FIELD}"}`]: {
        color: darkTheme.palette.secondary.main,
        labelKey: "sag_plots.TENSION_AT_THE_LOWER_SUPPORT_INCLINATION",
      },
      [`{"_field":"${SAG_TENSION_UPPER_SUPPORT_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
        labelKey: "sag_plots.TENSION_AT_THE_UPPER_SUPPORT_INCLINATION",
      },
      [`{"_field":"${SAG_THERMAL_HORIZONTAL_TENSION_FIELD}"}`]: {
        color: darkTheme.palette.extras.cinnabar.main,
        labelKey: "sag_plots.HORIZONTAL_TENSION_THERMAL",
      },
      [`{"_field":"${SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD}"}`]: {
        color: darkTheme.palette.extras.marigold.main,
        labelKey: "sag_plots.TENSION_AT_THE_LOWER_SUPPORT_THERMAL",
      },
      [`{"_field":"${SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD}"}`]: {
        color: darkTheme.palette.extras.tangerine.main,
        labelKey: "sag_plots.TENSION_AT_THE_UPPER_SUPPORT_THERMAL",
      },
    },
  },
})

export const dualConductorTensionPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: dualConductorTensionPlotView,
  reqConfig: {
    fields: DUAL_CONDUCTOR_TENSION_FIELDS,
    aggregate: ["max"],
    group: ["id", "_field"],
  },
  tabsConfig: dualConductorTensionTabsConfig,
})

const standardConductorTensionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Conductor tension",
  nameKey: "CONDUCTOR_TENSION",
  helpTooltip: [
    "plots_descriptions.HORIZONTAL_TENSION",
    "plots_descriptions.TENSION_AT_LOWER_SUPPORT",
    "plots_descriptions.TENSION_AT_UPPER_SUPPORT",
  ],
  dialogTitleKey: "plots_descriptions.CONDUCTOR_TENSION_DIAGRAM.TITLE",
  dialogContent: () => <ConductorTensionDialogContent />,
  properties: {
    axes: {
      y: getFieldFormat(STANDARD_CONDUCTOR_TENSION_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${SAG_HORIZONTAL_TENSION_FIELD}"}`]: {
        color: darkTheme.palette.primary.main,
      },
      [`{"_field":"${SAG_TENSION_LOWER_SUPPORT_FIELD}"}`]: {
        color: darkTheme.palette.secondary.main,
      },
      [`{"_field":"${SAG_TENSION_UPPER_SUPPORT_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
      },
    },
  },
})

export const standardConductorTensionPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: standardConductorTensionPlotView,
  reqConfig: {
    fields: STANDARD_CONDUCTOR_TENSION_FIELDS,
    aggregate: ["max"],
    group: ["id", "_field"],
  },
})

const conductorTensionMultiReqConfigBase = {
  method: ["last"],
  group: ["id", "_field"],
}

const conductorTensionMultiViewBase: Omit<MultiValueStatPlotView, "name" | "nameKey"> = {
  type: "multi-value-stat",
  properties: {
    rows: [],
    ...getFieldFormat(DUAL_CONDUCTOR_TENSION_FIELDS),
    decimalPlaces: 0,
  },
}

export const lastHorizontalTensionMultiDef: DevicePlotDef = {
  ...singleStatPlotDefBase,
  view: {
    ...conductorTensionMultiViewBase,
    name: "Last horizontal tension",
    nameKey: "LAST_HORIZONTAL_TENSION",
    properties: {
      ...conductorTensionMultiViewBase.properties,
      rows: [
        {
          field: SAG_HORIZONTAL_TENSION_FIELD,
          labelKey: "sag_plots.INCLINATION_BASED",
          color: darkTheme.palette.primary.main,
        },
        {
          field: SAG_THERMAL_HORIZONTAL_TENSION_FIELD,
          labelKey: "sag_plots.THERMAL_BASED",
          color: darkTheme.palette.extras.cinnabar.main,
        },
      ],
    },
  } as MultiValueStatPlotView,
  reqConfig: {
    fields: [SAG_HORIZONTAL_TENSION_FIELD, SAG_THERMAL_HORIZONTAL_TENSION_FIELD],
    ...conductorTensionMultiReqConfigBase,
  },
}

export const lastTensionAtLowerSupportMultiDef: DevicePlotDef = {
  ...singleStatPlotDefBase,
  view: {
    ...conductorTensionMultiViewBase,
    name: "Last tension at lower support",
    nameKey: "LAST_TENSION_AT_LOWER_SUPPORT",
    properties: {
      ...conductorTensionMultiViewBase.properties,
      rows: [
        {
          field: SAG_TENSION_LOWER_SUPPORT_FIELD,
          labelKey: "sag_plots.INCLINATION_BASED",
          color: darkTheme.palette.secondary.main,
        },
        {
          field: SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD,
          labelKey: "sag_plots.THERMAL_BASED",
          color: darkTheme.palette.extras.marigold.main,
        },
      ],
    },
  } as MultiValueStatPlotView,
  reqConfig: {
    fields: [SAG_TENSION_LOWER_SUPPORT_FIELD, SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD],
    ...conductorTensionMultiReqConfigBase,
  },
}

export const lastTensionAtUpperSupportMultiDef: DevicePlotDef = {
  ...singleStatPlotDefBase,
  view: {
    ...conductorTensionMultiViewBase,
    name: "Last tension at upper support",
    nameKey: "LAST_TENSION_AT_UPPER_SUPPORT",
    properties: {
      ...conductorTensionMultiViewBase.properties,
      rows: [
        {
          field: SAG_TENSION_UPPER_SUPPORT_FIELD,
          labelKey: "sag_plots.INCLINATION_BASED",
          color: darkTheme.palette.extras.limeGreen.main,
        },
        {
          field: SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD,
          labelKey: "sag_plots.THERMAL_BASED",
          color: darkTheme.palette.extras.tangerine.main,
        },
      ],
    },
  } as MultiValueStatPlotView,
  reqConfig: {
    fields: [SAG_TENSION_UPPER_SUPPORT_FIELD, SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD],
    ...conductorTensionMultiReqConfigBase,
  },
}

// ─── Current ──────────────────────────────────────────────────────────────────

const currentPlotView: LinePlotView = fp.merge(lineView, {
  name: "Current",
  nameKey: "CURRENT",
  properties: {
    axes: {
      y: getFieldFormat([DEVICE_CURRENT_FIELD]),
    },
  },
})

export const currentPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: currentPlotView,
  reqConfig: {
    fields: [DEVICE_CURRENT_FIELD],
    aggregate: ["max", "mean"],
    group: ["id", "_field"],
  },
})

const lastCurrentPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last current",
  nameKey: "LAST_CURRENT",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([DEVICE_CURRENT_FIELD]),
    size: "lg",
  },
})

export const lastCurrentPlotDef: DevicePlotDef = fp.merge(singleStatPlotDefBase, {
  view: lastCurrentPlotView,
  reqConfig: {
    fields: [DEVICE_CURRENT_FIELD],
    method: ["last"],
    group: ["id", "_field"],
  },
})

// ─── Weather station temperature ──────────────────────────────────────────────

const wStationTemperaturePlotView: LinePlotView = fp.merge(lineView, {
  name: "Ambient temperature",
  nameKey: "WEATHER_STATION_TEMPERATURE",
  properties: {
    axes: {
      y: {
        ...getFieldFormat([WSTATION_TEMPERATURE_FIELD]),
      },
    },
  },
})

export const wStationTemperaturePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: wStationTemperaturePlotView,
  reqConfig: {
    fields: [WSTATION_TEMPERATURE_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastWStationTemperaturePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last temperature",
  nameKey: "LAST_WEATHER_STATION_TEMPERATURE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSTATION_TEMPERATURE_FIELD]),
    size: "lg",
  },
})

export const lastWStationTemperaturePlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationTemperaturePlotView,
    reqConfig: {
      fields: [WSTATION_TEMPERATURE_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Weather station humidity ─────────────────────────────────────────────────

const wStationHumidityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Humidity",
  nameKey: "WEATHER_STATION_HUMIDITY",
  properties: {
    axes: {
      y: {
        bounds: ["0", "100"],
        ...getFieldFormat([WSTATION_HUMIDITY_FIELD]),
      },
    },
  },
})

export const wStationHumidityPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: wStationHumidityPlotView,
  reqConfig: {
    fields: [WSTATION_HUMIDITY_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastWStationHumidityPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last humidity",
  nameKey: "LAST_WEATHER_STATION_HUMIDITY",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSTATION_HUMIDITY_FIELD]),
    size: "lg",
  },
})

export const lastWStationHumidityPlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationHumidityPlotView,
    reqConfig: {
      fields: [WSTATION_HUMIDITY_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Weather station pressure ─────────────────────────────────────────────────

const wStationPressurePlotView: LinePlotView = fp.merge(lineView, {
  name: "Pressure",
  nameKey: "WEATHER_STATION_PRESSURE",
  properties: {
    axes: {
      y: {
        ...getFieldFormat([WSTATION_PRESSURE_FIELD]),
        bounds: ["90000", "105000"],
      },
    },
  },
})

export const wStationPressurePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: wStationPressurePlotView,
  reqConfig: {
    fields: [WSTATION_PRESSURE_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastWStationPressurePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last pressure",
  nameKey: "LAST_WEATHER_STATION_PRESSURE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSTATION_PRESSURE_FIELD]),
    decimalPlaces: 0,
    size: "lg",
  },
})

export const lastWStationPressurePlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationPressurePlotView,
    reqConfig: {
      fields: [WSTATION_PRESSURE_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Weather station precipitation intensity ──────────────────────────────────

const wStationPrecipitationIntensityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Precipitation Intensity",
  nameKey: "WEATHER_STATION_PRECIPITATION_INTENSITY",
  properties: {
    axes: {
      y: {
        ...getFieldFormat([WSTATION_PRECIPITATION_FIELD]),
        bounds: ["0", "100"],
      },
    },
  },
})

export const wStationPrecipitationIntensityPlotDef: DevicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wStationPrecipitationIntensityPlotView,
    reqConfig: {
      fields: [WSTATION_PRECIPITATION_FIELD],
      aggregate: ["max"],
      group: ["id", "_field"],
    },
  },
)

const lastWStationPrecipitationIntensityPlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last precipitation intensity",
    nameKey: "LAST_WEATHER_STATION_PRECIPITATION_INTENSITY",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSTATION_PRECIPITATION_FIELD]),
      size: "lg",
    },
  },
)

export const lastWStationPrecipitationIntensityPlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationPrecipitationIntensityPlotView,
    reqConfig: {
      fields: [WSTATION_PRECIPITATION_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Weather station solar irradiance ─────────────────────────────────────────

const wStationSolarIrradiancePlotView: LinePlotView = fp.merge(lineView, {
  name: "Solar irradiance",
  nameKey: "WEATHER_STATION_SOLAR_IRRADIANCE",
  properties: {
    axes: {
      y: {
        ...getFieldFormat([WSTATION_SOLAR_IRRADIANCE_FIELD]),
      },
    },
  },
})

export const wStationSolarIrradiancePlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: wStationSolarIrradiancePlotView,
  reqConfig: {
    fields: [WSTATION_SOLAR_IRRADIANCE_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastWStationSolarIrradiancePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last solar irradiance",
  nameKey: "LAST_WEATHER_STATION_SOLAR_IRRADIANCE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSTATION_SOLAR_IRRADIANCE_FIELD]),
    size: "lg",
  },
})

export const lastWStationSolarIrradiancePlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationSolarIrradiancePlotView,
    reqConfig: {
      fields: [WSTATION_SOLAR_IRRADIANCE_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Weather station wind direction ───────────────────────────────────────────

const wStationWindDirectionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind Direction",
  nameKey: "WEATHER_STATION_WIND_DIRECTION",
  helpTooltip: ["plots_descriptions.WEATHER_STATION_WIND_DIRECTION"],
  properties: {
    axes: {
      y: {
        ...getFieldFormat([WSTATION_WIND_DIRECTION_FIELD]),
        bounds: ["0", "360"],
      },
    },
    tooltip: {
      trigger: "axis",
      customComponent: ({ trigger }: CustomTooltipProps) => (
        <MuiWSDegreesRadiansTooltip trigger={trigger} />
      ),
    },
  },
})

export const wStationWindDirectionPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: wStationWindDirectionPlotView,
  reqConfig: {
    fields: [WSTATION_WIND_DIRECTION_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
    fieldConversions: {
      [WSTATION_WIND_DIRECTION_FIELD]: "radiansToDegrees",
    },
  },
})

const lastWStationWindDirectionPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind direction",
  nameKey: "LAST_WEATHER_STATION_WIND_DIRECTION",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSTATION_WIND_DIRECTION_FIELD]),
    decimalPlaces: 0,
    size: "lg",
  },
})

export const lastWStationWindDirectionPlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationWindDirectionPlotView,
    reqConfig: {
      fields: [WSTATION_WIND_DIRECTION_FIELD],
      method: ["last"],
      group: ["id", "_field"],
      fieldConversions: {
        [WSTATION_WIND_DIRECTION_FIELD]: "radiansToDegrees",
      },
    },
  },
)

// ─── Weather station wind speed ───────────────────────────────────────────────

const wStationWindSpeedPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind Speed",
  nameKey: "WEATHER_STATION_WIND_SPEED",
  properties: {
    axes: {
      y: {
        ...getFieldFormat([WSTATION_WIND_SPEED_FIELD]),
      },
    },
  },
})

export const wStationWindSpeedPlotDef: DevicePlotDef = fp.merge(linePlotDefBase, {
  view: wStationWindSpeedPlotView,
  reqConfig: {
    fields: [WSTATION_WIND_SPEED_FIELD],
    aggregate: ["mean"],
    group: ["id", "_field"],
  },
})

const lastWStationWindSpeedPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind speed",
  nameKey: "LAST_WEATHER_STATION_WIND_SPEED",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSTATION_WIND_SPEED_FIELD]),
    size: "lg",
  },
})

export const lastWStationWindSpeedPlotDef: DevicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWStationWindSpeedPlotView,
    reqConfig: {
      fields: [WSTATION_WIND_SPEED_FIELD],
      method: ["last"],
      group: ["id", "_field"],
    },
  },
)

// ─── Weather service ────────────────────────────────────────────────
// Third-party weather-service data keyed by the device's own location geohash.

const wServiceAmbientDataSource = {
  entity: "device",
  resolver: "self",
  service: "weather",
} as const

// Wind speed
const wServiceAmbientWindSpeedPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind speed",
  nameKey: "WEATHER_SERVICE_WIND_SPEED",
  properties: {
    axes: { y: getFieldFormat([WSERVICE_WIND_SPEED_FIELD]) },
  },
})

export const wServiceAmbientWindSpeedPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceAmbientWindSpeedPlotView,
    dataSource: wServiceAmbientDataSource,
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_WIND_SPEED_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceAmbientWindSpeedPlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last wind speed",
    nameKey: "LAST_WEATHER_SERVICE_WIND_SPEED",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSERVICE_WIND_SPEED_FIELD]),
      size: "lg",
    },
  },
)

export const lastWServiceAmbientWindSpeedPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceAmbientWindSpeedPlotView,
    dataSource: wServiceAmbientDataSource,
    reqConfig: {
      fields: [WSERVICE_WIND_SPEED_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// Wind gusts
const wServiceAmbientWindGustPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind gusts",
  nameKey: "WEATHER_SERVICE_WIND_GUSTS",
  properties: {
    axes: { y: getFieldFormat([WSERVICE_WIND_GUSTS_FIELD]) },
  },
})

export const wServiceAmbientWindGustPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceAmbientWindGustPlotView,
    dataSource: wServiceAmbientDataSource,
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_WIND_GUSTS_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceAmbientWindGustPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last wind gusts",
  nameKey: "LAST_WEATHER_SERVICE_WIND_GUSTS",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([WSERVICE_WIND_GUSTS_FIELD]),
    size: "lg",
  },
})

export const lastWServiceAmbientWindGustPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceAmbientWindGustPlotView,
    dataSource: wServiceAmbientDataSource,
    reqConfig: {
      fields: [WSERVICE_WIND_GUSTS_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// Wind direction
const wServiceAmbientWindDirectionPlotView: LinePlotView = fp.merge(lineView, {
  name: "Wind direction",
  nameKey: "WEATHER_SERVICE_WIND_DIRECTION",
  helpTooltip: ["plots_descriptions.WEATHER_SERVICE_WIND_DIRECTION"],
  properties: {
    axes: { y: getFieldFormat([WSERVICE_WIND_DIRECTION_FIELD]) },
  },
})

export const wServiceAmbientWindDirectionPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceAmbientWindDirectionPlotView,
    dataSource: wServiceAmbientDataSource,
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_WIND_DIRECTION_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceAmbientWindDirectionPlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last wind direction",
    nameKey: "LAST_WEATHER_SERVICE_WIND_DIRECTION",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSERVICE_WIND_DIRECTION_FIELD]),
      decimalPlaces: 0,
      size: "lg",
    },
  },
)

export const lastWServiceAmbientWindDirectionPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceAmbientWindDirectionPlotView,
    dataSource: wServiceAmbientDataSource,
    reqConfig: {
      fields: [WSERVICE_WIND_DIRECTION_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// Temperature
const wServiceAmbientTemperaturePlotView: LinePlotView = fp.merge(lineView, {
  name: "Ambient temperature",
  nameKey: "WEATHER_SERVICE_TEMPERATURE",
  properties: {
    axes: { y: getFieldFormat([WSERVICE_TEMPERATURE_FIELD]) },
  },
})

export const wServiceAmbientTemperaturePlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceAmbientTemperaturePlotView,
    dataSource: wServiceAmbientDataSource,
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_TEMPERATURE_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceAmbientTemperaturePlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last temperature",
    nameKey: "LAST_WEATHER_SERVICE_TEMPERATURE",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSERVICE_TEMPERATURE_FIELD]),
      size: "lg",
    },
  },
)

export const lastWServiceAmbientTemperaturePlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceAmbientTemperaturePlotView,
    dataSource: wServiceAmbientDataSource,
    reqConfig: {
      fields: [WSERVICE_TEMPERATURE_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// Relative humidity
const wServiceAmbientRelativeHumidityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Relative humidity",
  nameKey: "WEATHER_SERVICE_RELATIVE_HUMIDITY",
  properties: {
    axes: { y: { bounds: ["0", "100"], ...getFieldFormat([WSERVICE_HUMIDITY_FIELD]) } },
  },
})

export const wServiceAmbientRelativeHumidityPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceAmbientRelativeHumidityPlotView,
    dataSource: wServiceAmbientDataSource,
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_HUMIDITY_FIELD],
      aggregate: ["mean"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceAmbientRelativeHumidityPlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last relative humidity",
    nameKey: "LAST_WEATHER_SERVICE_RELATIVE_HUMIDITY",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSERVICE_HUMIDITY_FIELD]),
      size: "lg",
    },
  },
)

export const lastWServiceAmbientRelativeHumidityPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceAmbientRelativeHumidityPlotView,
    dataSource: wServiceAmbientDataSource,
    reqConfig: {
      fields: [WSERVICE_HUMIDITY_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

// Precipitation
const wServiceAmbientPrecipitationPlotView: LinePlotView = fp.merge(lineView, {
  name: "Precipitation",
  nameKey: "WEATHER_SERVICE_PRECIPITATION",
  properties: {
    axes: {
      y: { bounds: ["0", "10"], ...getFieldFormat([WSERVICE_PRECIPITATION_FIELD]) },
    },
  },
})

export const wServiceAmbientPrecipitationPlotDef: WeatherServicePlotDef = fp.merge(
  linePlotDefBase,
  {
    view: wServiceAmbientPrecipitationPlotView,
    dataSource: wServiceAmbientDataSource,
    attribution: OPEN_METEO_ATTRIBUTION,
    reqConfig: {
      fields: [WSERVICE_PRECIPITATION_FIELD],
      aggregate: ["max"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)

const lastWServiceAmbientPrecipitationPlotView: SingleStatPlotView = fp.merge(
  singleStatView,
  {
    name: "Last precipitation",
    nameKey: "LAST_WEATHER_SERVICE_PRECIPITATION",
    properties: {
      footerFields: ["_time"],
      ...getFieldFormat([WSERVICE_PRECIPITATION_FIELD]),
      size: "lg",
    },
  },
)

export const lastWServiceAmbientPrecipitationPlotDef: WeatherServicePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastWServiceAmbientPrecipitationPlotView,
    dataSource: wServiceAmbientDataSource,
    reqConfig: {
      fields: [WSERVICE_PRECIPITATION_FIELD],
      method: ["last"],
      group: ["_field", "geohash"],
      measurement: "current_weather",
    },
  },
)
